import { logger } from "@/utils/logger";

import SecureStorage from './secureStorage';
import { supabase } from '../integrations/supabase/client';

export interface DealerData {
  DealerName: string;
  DealerID: string;
  DealerLogoURL: string;
  ServiceFormFr: string;
  ServiceFormEng: string;
}

const DEALER_DATA_KEY = 'dealerData';

export const readDealerCookie = (): DealerData | null => {
  try {
    const cookieValue = getCookie('d2cchatbot');
    if (!cookieValue) {
      logger.log('No d2cchatbot cookie found');
      return null;
    }

    const dealerData = JSON.parse(decodeURIComponent(cookieValue)) as DealerData;
    // Dealer data loaded from cookie
    return dealerData;
  } catch (error) {
    console.error('Error parsing d2cchatbot cookie:', error);
    return null;
  }
};

export const storeDealerData = async (dealerData: DealerData): Promise<void> => {
  try {
    await SecureStorage.setItem(DEALER_DATA_KEY, dealerData);
    // Dealer data stored securely
  } catch (error) {
    console.error('Error storing dealer data:', error);
  }
};

export const getDealerData = async (): Promise<DealerData | null> => {
  try {
    const stored = await SecureStorage.getItem(DEALER_DATA_KEY);
    if (!stored) return null;
    return stored as DealerData;
  } catch (error) {
    console.error('Error retrieving dealer data:', error);
    return null;
  }
};

export const getDealerLogoURL = async (): Promise<string | null> => {
  // First check the d2cchatbot cookie for logo URL
  const cookieData = readDealerCookie();
  if (cookieData?.DealerLogoURL) {
    let logoURL = cookieData.DealerLogoURL;
    
    // Skip blank/placeholder logos - fall through to DOM fallback
    const lowerLogo = logoURL.toLowerCase();
    if (!lowerLogo.includes('blank.') && !lowerLogo.includes('placeholder.') && !lowerLogo.includes('default.')) {
      // Fix relative URLs by adding https:// if missing
      if (logoURL && !logoURL.startsWith('http://') && !logoURL.startsWith('https://')) {
        logoURL = `https://${logoURL}`;
      }
      return logoURL;
    }
    logger.log('⚠️ Cookie logo is a placeholder, falling through to DOM fallback');
  }
  
  // Fallback to secure storage
  const dealerData = await getDealerData();
  if (dealerData?.DealerLogoURL) {
    let logoURL = dealerData.DealerLogoURL;
    
    // Skip blank/placeholder logos - fall through to DOM fallback
    const lowerStoredLogo = logoURL.toLowerCase();
    if (lowerStoredLogo.includes('blank.') || lowerStoredLogo.includes('placeholder.') || lowerStoredLogo.includes('default.')) {
      logger.log('⚠️ Stored logo is a placeholder, falling through to DOM fallback');
    } else {
      // Fix relative URLs by adding https:// if missing
      if (!logoURL.startsWith('http://') && !logoURL.startsWith('https://')) {
        logoURL = `https://${logoURL}`;
      }
      return logoURL;
    }
  }
  
  // Fallback: try to grab logo from the parent page's DOM
  try {
    const parentDoc = window.parent?.document || document;
    
    // Try #dealer_logo first (but skip placeholders)
    const dealerLogoEl = parentDoc.getElementById('dealer_logo') as HTMLImageElement | null;
    if (dealerLogoEl?.src) {
      const lowerDomLogo = dealerLogoEl.src.toLowerCase();
      if (!lowerDomLogo.includes('blank.') && !lowerDomLogo.includes('placeholder.') && !lowerDomLogo.includes('default.')) {
        logger.log('🖼️ Logo fallback: using #dealer_logo from parent page');
        return dealerLogoEl.src;
      }
      logger.log('⚠️ #dealer_logo is a placeholder, trying .otherLogoMenuImage');
    }
    
    // Try .otherLogoMenuImage (alternate logo element on some dealer sites)
    const otherLogoEl = parentDoc.querySelector('.otherLogoMenuImage') as HTMLImageElement | null;
    if (otherLogoEl) {
      const src = otherLogoEl.getAttribute('src') || otherLogoEl.getAttribute('data-imgsrc');
      if (src) {
        logger.log('🖼️ Logo fallback: using .otherLogoMenuImage from parent page');
        // Resolve relative URLs against the parent page origin
        if (src.startsWith('/')) {
          return `${window.parent.location.origin}${src}`;
        }
        return src;
      }
    }
  } catch (e) {
    // Cross-origin access may fail silently
  }
  
  return null;
};

// Central function to get dealer ID - uses DealerManager
export const getDealerID = async (): Promise<number> => {
  try {
    // Import dynamically to avoid circular dependency
    const { DealerManager } = await import('./dealerManager');
    const { DealerCache } = await import('./dealerCache');
    
    // Check if authentication is already in progress
    if (DealerCache.isCurrentlyLoading()) {
      const waitResult = await DealerCache.waitForLoad();
      if (waitResult) {
        return waitResult.dealer_id;
      }
    }
    
    // Get dealer info from centralized manager
    const dealerInfo = DealerManager.getDealerInfo();
    
    if (dealerInfo) {
      return dealerInfo.dealer_id;
    }
    
    // If not cached, initialize and get dealer ID
    logger.log('🔄 getDealerID - No cached dealer, initializing...');
    const config = await DealerManager.initializeFromGlobalSettings();
    // Dealer ID initialized
    return config.dealer_id;
    
  } catch (error) {
    console.error('❌ getDealerID error:', error);
    throw error;
  }
};

// Synchronous function that returns a default value - FOR LEGACY COMPATIBILITY ONLY
// New code should use the async getDealerID() function
export const getDealerIDSync = (): string => {
  logger.warn('⚠️ getDealerIDSync is deprecated - use async getDealerID() instead');
  return 'placeholder-dealer-id';
};

export const formatDealerInfoForOpenAI = (dealerData: DealerData): string => {
  return `For your information, here are important data points for you to remember during our conversation: ${JSON.stringify(dealerData, null, 2)}`;
};

// Helper function to get cookie (using existing getCookie from cookieUtils)
const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
};
